import type { Task } from 'redux-saga';
import { call, fork } from 'redux-saga/effects';

/**
 * Typed wrappers around redux-saga `call`/`fork` that work well with generator sagas.
 *
 * redux-saga's TS overloads can sometimes select the wrong signature when passing generator
 * functions directly, leading to "no overload matches" errors. These helpers centralize the
 * (unavoidable) escape hatch in one place, while keeping call sites type-safe.
 */

export function* callSaga<TArgs extends unknown[], TResult>(
  saga: (...args: TArgs) => Generator<unknown, TResult, unknown>,
  ...args: TArgs
): Generator<unknown, TResult, unknown> {
  // redux-saga's Effect typings don't model generator sagas precisely.
  // Centralize the minimal escape hatch here so call sites stay type-safe.
  return (yield call(saga as unknown as (...args: TArgs) => unknown, ...args)) as TResult;
}

export function* forkSaga<TArgs extends unknown[]>(
  saga: (...args: TArgs) => Generator,
  ...args: TArgs
): Generator<unknown, Task, unknown> {
  // See callSaga note above.
  return (yield fork(saga as unknown as (...args: TArgs) => Generator, ...args)) as Task;
}
